import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import VehicleCard from '../../components/VehicleCard';
import { categoryAPI, wishlistAPI, getUserRole, isAuthenticated } from '../../config/api';
import { showAppToast } from '../../utils/notifications';

function ListProducts({ categories }) {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [sortBy, setSortBy] = useState('name');
  const [fuelType, setFuelType] = useState('all');
  const [transmission, setTransmission] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const role = getUserRole();

  const category = useMemo(
    () => (categories || []).find((item) => String(item.id) === String(id)),
    [categories, id]
  );

  const fetchCategoryData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await categoryAPI.getProducts(id);
      setVehicles(response.data || []);
    } catch (error) {
      showAppToast({ message: 'Failed to load category vehicles', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCategoryData();
  }, [fetchCategoryData]);

  const filteredVehicles = useMemo(() => {
    let filtered = [...vehicles];

    if (searchQuery) {
      const term = searchQuery.toLowerCase();
      filtered = filtered.filter((vehicle) =>
        vehicle.name?.toLowerCase().includes(term) ||
        vehicle.make?.toLowerCase().includes(term) ||
        vehicle.model?.toLowerCase().includes(term) ||
        vehicle.description?.toLowerCase().includes(term)
      );
    }

    filtered = filtered.filter((vehicle) => {
      const price = Number(vehicle.price || 0);
      return price >= priceRange[0] && price <= priceRange[1];
    });

    if (fuelType !== 'all') {
      filtered = filtered.filter((vehicle) =>
        vehicle.fuel_type?.toLowerCase() === fuelType.toLowerCase()
      );
    }

    if (transmission !== 'all') {
      filtered = filtered.filter((vehicle) =>
        vehicle.transmission?.toLowerCase() === transmission.toLowerCase()
      );
    }

    filtered.sort((a, b) => {
      if (sortBy === 'price-low') return Number(a.price || 0) - Number(b.price || 0);
      if (sortBy === 'price-high') return Number(b.price || 0) - Number(a.price || 0);
      return (a.name || '').localeCompare(b.name || '');
    });

    return filtered;
  }, [vehicles, searchQuery, priceRange, sortBy, fuelType, transmission]);

  const resetFilters = () => {
    setSearchQuery('');
    setPriceRange([0, 1000]);
    setFuelType('all');
    setTransmission('all');
    setSortBy('name');
  };

  const handleAddToWishlist = async (vehicleId) => {
    if (!isAuthenticated()) {
      showAppToast({ message: 'Please login to add items to wishlist', type: 'info' });
      return;
    }

    try {
      await wishlistAPI.add({ productId: vehicleId });
      showAppToast({ message: 'Vehicle added to wishlist', type: 'success' });
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to add to wishlist', type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-primary-theme">
        <div className="h-16 w-16 animate-spin rounded-full border-b-2 border-t-2 border-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-theme px-4 py-8 text-primary-theme">
      <div className="mx-auto max-w-7xl">
        <nav className="mb-6 text-sm">
          <Link to="/home" className="text-accent hover:underline">Home</Link>
          <span className="mx-2 text-secondary-theme">/</span>
          <Link to="/vehicles" className="text-accent hover:underline">Vehicles</Link>
          <span className="mx-2 text-secondary-theme">/</span>
          <span className="text-secondary-theme">{category?.categoryName || 'Category'}</span>
        </nav>

        <header className="card-luxury mb-8 rounded-lg p-6">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
            <div>
              <h1 className="section-title text-3xl">{category?.categoryName || 'Category Vehicles'}</h1>
              {category?.description && (
                <p className="mt-2 max-w-3xl text-secondary-theme">{category.description}</p>
              )}
              <p className="mt-4 text-secondary-theme">
                <span className="font-semibold text-accent">{filteredVehicles.length}</span> vehicles found
                {searchQuery && <span> for "{searchQuery}"</span>}
              </p>
            </div>
            {role === 'admin' && (
              <Link to="/admin/vehicle/new" className="btn-luxury">
                Add Vehicle
              </Link>
            )}
          </div>
        </header>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          <aside className="lg:col-span-1">
            <div className="card-luxury sticky top-24 rounded-lg p-6">
              <h2 className="section-title mb-5 text-xl">Filters</h2>

              <label htmlFor="category-search" className="mb-2 block text-sm font-semibold">Search</label>
              <input
                id="category-search"
                type="text"
                placeholder="Search vehicles..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="form-control-luxury mb-6 w-full rounded-lg px-3 py-2"
              />

              <label htmlFor="category-price" className="mb-2 block text-sm font-semibold">
                Price Range: ${priceRange[0]} - ${priceRange[1]}
              </label>
              <input
                id="category-price"
                type="range"
                min="0"
                max="1000"
                step="10"
                value={priceRange[1]}
                onChange={(event) => setPriceRange([priceRange[0], Number(event.target.value)])}
                className="mb-6 w-full accent-[rgb(212,175,55)]"
              />

              <label htmlFor="category-fuel" className="mb-2 block text-sm font-semibold">Fuel Type</label>
              <select
                id="category-fuel"
                value={fuelType}
                onChange={(event) => setFuelType(event.target.value)}
                className="form-control-luxury mb-6 w-full rounded-lg px-3 py-2"
              >
                <option value="all">All Types</option>
                <option value="petrol">Petrol</option>
                <option value="diesel">Diesel</option>
                <option value="electric">Electric</option>
                <option value="hybrid">Hybrid</option>
              </select>

              <label htmlFor="category-transmission" className="mb-2 block text-sm font-semibold">Transmission</label>
              <select
                id="category-transmission"
                value={transmission}
                onChange={(event) => setTransmission(event.target.value)}
                className="form-control-luxury mb-6 w-full rounded-lg px-3 py-2"
              >
                <option value="all">All Types</option>
                <option value="automatic">Automatic</option>
                <option value="manual">Manual</option>
              </select>

              <button type="button" onClick={resetFilters} className="btn-luxury-outline w-full">
                Reset Filters
              </button>
            </div>
          </aside>

          <main className="lg:col-span-3">
            <div className="card-luxury mb-6 flex flex-col justify-between gap-4 rounded-lg p-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3">
                <label htmlFor="category-sort" className="text-sm font-semibold">Sort by:</label>
                <select
                  id="category-sort"
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  className="form-control-luxury rounded-lg px-3 py-2"
                >
                  <option value="name">Name</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                </select>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setViewMode('grid')}
                  className={viewMode === 'grid' ? 'btn-luxury' : 'btn-luxury-outline'}
                  aria-label="Grid View"
                >
                  <i className="bi bi-grid" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'btn-luxury' : 'btn-luxury-outline'}
                  aria-label="List View"
                >
                  <i className="bi bi-list" />
                </button>
              </div>
            </div>

            {filteredVehicles.length === 0 ? (
              <div className="card-luxury rounded-lg p-12 text-center">
                <i className="bi bi-car-front text-6xl text-secondary-theme" />
                <h3 className="section-title mt-4 text-xl">No vehicles found</h3>
                <p className="mt-2 text-secondary-theme">Try adjusting your filters or search query.</p>
                <button type="button" onClick={resetFilters} className="btn-luxury mt-6">
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3' : 'space-y-6'}>
                {filteredVehicles.map((vehicle) => (
                  <VehicleCard
                    key={vehicle.id}
                    product={vehicle}
                    categoryName={category?.categoryName}
                    role={role}
                    viewMode={viewMode}
                    onWishlist={handleAddToWishlist}
                  />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default ListProducts;
